// ReportsNav — sidebar nav for the Reports module.
//
// Unlike the other module navs (which group by section label), Reports
// groups its entries by ORIGINATING MODULE — Collections / Skip Management /
// Weighbridge — and each group has a manual collapse/expand toggle. Groups
// default to expanded; the collapsed state persists in localStorage per
// group key.
//
// Link / list styling is reused from CollectionsNav.module.css so the rail
// renders identically to every other module; ReportsNav.module.css only adds
// the collapsible group-header button.

import { type ComponentType, useCallback, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  BarChart3,
  ChevronDown,
  ChevronRight,
  Container,
  Database,
  FileText,
  History,
  Scale,
  TrendingUp,
  Truck,
  Users,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useAuthStore } from '../../store/authStore';
import { useSidebar } from '../../components/shell/SidebarContext';
import type { UserSummary } from '../../types/api';
import navStyles from '../collections/components/CollectionsNav.module.css';
import styles from './ReportsNav.module.css';

type Role = UserSummary['role'];

export interface ReportsNavItem {
  to: string;
  label: string;
  icon: ComponentType<LucideProps>;
  roles?: Role[];
  end?: boolean;
}

export interface ReportsNavGroup {
  key: string;
  label: string;
  icon: ComponentType<LucideProps>;
  items: ReportsNavItem[];
}

const STAFF_ROLES: Role[] = ['staff', 'admin'];
const ADMIN_ROLES: Role[] = ['admin'];
// Skip Management reports are deliberately tighter than the other report
// groups — admin + manager only (no plain staff). Matches the route + server
// gate.
const MANAGER_ROLES: Role[] = ['admin', 'manager'];

// Single source of truth for the Reports module — consumed by both the
// sidebar and the dashboard index (report counts + first-report links).
export const REPORTS_GROUPS: ReportsNavGroup[] = [
  {
    key: 'collections',
    label: 'Collections',
    icon: Truck,
    items: [
      { to: '/reports/collections', label: 'Reports', icon: BarChart3, end: true, roles: STAFF_ROLES },
      { to: '/reports/collections/metrics', label: 'Metrics', icon: BarChart3, roles: ADMIN_ROLES },
      { to: '/reports/collections/receipts-export', label: 'Bulk Export Receipts', icon: FileText, roles: ADMIN_ROLES },
      { to: '/reports/collections/audit-log', label: 'Audit Log', icon: History, roles: ADMIN_ROLES },
      { to: '/reports/collections/data-export', label: 'Data Export', icon: Database, roles: ADMIN_ROLES },
    ],
  },
  {
    key: 'skip-management',
    label: 'Skip Management',
    icon: Container,
    items: [
      { to: '/reports/skip-management', label: 'Reports', icon: BarChart3, end: true, roles: MANAGER_ROLES },
      { to: '/reports/skip-management/jobs', label: 'Jobs Overview', icon: BarChart3, roles: MANAGER_ROLES },
      { to: '/reports/skip-management/enquiries', label: 'Lead Conversion', icon: TrendingUp, roles: MANAGER_ROLES },
      { to: '/reports/skip-management/inventory', label: 'Inventory Utilisation', icon: Container, roles: MANAGER_ROLES },
      { to: '/reports/skip-management/customers', label: 'Customer Revenue', icon: Users, roles: MANAGER_ROLES },
    ],
  },
  {
    key: 'weighbridge',
    label: 'Weighbridge',
    icon: Scale,
    items: [
      { to: '/reports/weighbridge', label: 'Reports', icon: BarChart3, end: true, roles: STAFF_ROLES },
    ],
  },
];

const STORAGE_PREFIX = 'reports.nav.collapsed.';

function storageKey(groupKey: string): string {
  return `${STORAGE_PREFIX}${groupKey}`;
}

function readCollapsed(groupKey: string): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(storageKey(groupKey)) === 'true';
}

export function ReportsNav(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  const { isCollapsed } = useSidebar();

  // Lazy-init each group's collapsed state from localStorage. Default
  // expanded (no stored value === expanded).
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const g of REPORTS_GROUPS) initial[g.key] = readCollapsed(g.key);
    return initial;
  });

  const toggleGroup = useCallback((groupKey: string) => {
    setCollapsed((prev) => {
      const next = !prev[groupKey];
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(storageKey(groupKey), String(next));
      }
      return { ...prev, [groupKey]: next };
    });
  }, []);

  function visible(roles?: Role[]): boolean {
    if (!roles) return true;
    if (!role) return false;
    return roles.includes(role);
  }

  return (
    <div className={cn(navStyles.nav, isCollapsed && navStyles.collapsed)}>
      {REPORTS_GROUPS.map((group) => {
        const items = group.items.filter((it) => visible(it.roles));
        if (items.length === 0) return null;
        // When the whole rail is collapsed, drop the toggle (labels are
        // hidden anyway) and just render the icon-only links.
        const groupCollapsed = !isCollapsed && collapsed[group.key];
        return (
          <div key={group.key} className={navStyles.section}>
            {isCollapsed ? null : (
              <button
                type="button"
                className={styles.groupHeader}
                aria-expanded={!groupCollapsed}
                onClick={() => toggleGroup(group.key)}
              >
                <span className={styles.groupHeaderLabel}>
                  <group.icon size={14} aria-hidden />
                  {group.label}
                </span>
                <span className={styles.chevron} aria-hidden>
                  {groupCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                </span>
              </button>
            )}
            {groupCollapsed ? null : (
              <ul className={navStyles.list}>
                {items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        cn(navStyles.link, isActive && navStyles.linkActive)
                      }
                      title={isCollapsed ? item.label : undefined}
                    >
                      <span className={navStyles.linkInner}>
                        <span className={navStyles.icon}>
                          <item.icon size={18} aria-hidden />
                        </span>
                        <span className={navStyles.label}>{item.label}</span>
                      </span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
